import CompanyApi from '@/api/CompanyApi';
import CompanyCandidateCodeApi from '@/api/CompanyCandidateCodeApi';
import MembershipApi from '@/api/MembershipApi';
import AjaxCooler from '@/utils/AjaxCooler';
import { Reactive } from '@/utils/decorators';
import { delayRequestAsync } from '@/utils/delay-ajax';
import { useModal, useNotif } from '@orion.ui/orion';
import { defineAsyncComponent } from 'vue';
import useSelfService from './SelfService';

import { i18n } from '@/i18n';

class MembershipService {

	private cooler = new AjaxCooler(CompanyApi.get);
	@Reactive private readonly state = { relatedAgencies: [] as ICompanyDetailedModel[] };

	private get self () { return useSelfService().self }
	get relatedAgencies () { return this.state.relatedAgencies }

	async listAsync (forceRefresh = false, params: OmitUserId<ICompanyListByUserIdParameters>) {
		return await this.cooler.coolAsync(forceRefresh, params);
	}

	async addAsync (params: IAddMembershipModel) {
		try {
			const { data } = await MembershipApi.add(params);
			return data;
		}
		catch (e: any) {
			useNotif.danger(e?.response?.data);
		}
	}

	async deleteAsync (membershipId: number) {
		if (!this.self.organizationGuard()) return;

		try {
			await MembershipApi.delete(membershipId, this.self.organization.id);
		}
		catch (e: any) {
			useNotif.danger(e?.response?.data);
		}
	}

	async openTempWorkerAddMembershipModalAsync (code?: string, resolver?: () => void) {
		return new Promise<void>(async (resolve) => {
			useModal({
				Nested: defineAsyncComponent(() => import('@/components/onboarding/OnboardingTempWorkerAddMembershipModal.vue')),
				NestedProps: { code },
				size: 'sm',
				events: {
					'confirm': async (modal, payload: { id: number, name: undefined | string }) => {
						await this.confirmAddMembershipAsync(payload.id, payload.name);
						modal.close({ flush: true });
						(resolver ?? resolve)();
					},
				},
			});
		});
	}

	async confirmAddMembershipAsync (id: number, agencyName: undefined | string) {
		try {
			await MembershipApi.addForCandidate(id);
			useNotif.success(i18n.global.t('common.savingCompleted'), i18n.global.t('common.agency.linkSuccess', { agencyName }));
		}
		catch (error: any) {
			useNotif.danger(i18n.global.t('common.notification.oops'), error.response.data);
		}
	}

	async openTempWorkerPreventAddMembershipModalAsync () {
		useModal({
			Nested: defineAsyncComponent(() => import('@/components/onboarding/OnboardingTempWorkerPreventAddMembershipModal.vue')),
			size: 'sm',
		});
	}

	async openTempWorkerNoMembershipModalAsync () {
		const reminder = localStorage.getItem('remind-no-membership-modal');

		if (reminder === 'never') {
			return;
		}
		else if (reminder && /\d+/.test(reminder)) {
			const reminderDate = new Date(+reminder);
			if (reminderDate.valueOf() > new Date().valueOf()) return;
		}

		return new Promise<void>(async (resolve) => {
			useModal({
				Nested: defineAsyncComponent(() => import('@/components/onboarding/OnboardingTempWorkerNoMembershipModal.vue')),
				size: 'sm',
				hideClose: true,
				hideOnOverlayClick: false,
				events: {
					enterCode: () => this.openTempWorkerAddMembershipModalAsync(undefined, resolve),
					remindLater: (modal) => {
						const remindDate = new Date();
						// Set the reminder to next day
						remindDate.setDate(remindDate.getDate() + 1);
						localStorage.setItem('remind-no-membership-modal', remindDate.valueOf().toString());
						modal.close();
						resolve();
					},
				},
			});
		});
	}

	async openCompanyCodeForMemberShipShareModalAsync (id: number) {
		return new Promise<void>(async (resolve) => {
			useModal({
				Nested: defineAsyncComponent(() => import('@/components/onboarding/OnboardingCompanyCodeModal.vue')),
				NestedProps: { id },
				size: 'lg',
				events: {
					'shared': async (Modal, payload: any) => {
						await this.shareCodeToTempWorkerAsync(payload, id);
						// Modal.close();
						resolve();
					},
				},
			});
		});
	}

	async getCompanyByCodeAsync (code: string) {
		const { data } = await delayRequestAsync(CompanyApi.getByCode(code), 2000);
		return data;
	}

	async getCodeCompanyByCompanyIdAsync (id: number) {
		const { data } = await CompanyCandidateCodeApi.selectByCompanyId(id);
		return data;
	}

	async shareCodeToTempWorkerAsync (payload: any, companyId: number) {
		try {
			if (payload.selectedShareMethod === 'phone') {
				await CompanyCandidateCodeApi.shareBySms(payload.phone.phoneNumber, payload.phone.phoneCountryCode, companyId);
			}
			else {
				await CompanyCandidateCodeApi.shareByEmail(payload.email, companyId);
			}
			useNotif.success(i18n.global.t('common.sharingSuccessful'), i18n.global.t('common.code.shareSuccess'));
		}
		catch (error: any) {
			useNotif.danger(i18n.global.t('common.notification.oops'), error.response.data);
		}
	}

	async getRelatedAgenciesAsync () {
		const res = await CompanyApi.getDetailed();
		if (res?.data) {
			this.state.relatedAgencies = res.data;
		}
		return this.state.relatedAgencies;
	}

}

const membershipServiceSingleton = new MembershipService();

export default function useMembershipService () {
	return membershipServiceSingleton;
}
